/**
 * HOW YOU PAY, at `/account/paying`, a client's alone (slice A2). No artboard in
 * `faithfully-kept-business/design/home/` draws it yet; it is built from the Settings canvas's
 * Settings-Paying artboard, drawn in today's look, in the current tokens and parts, and goes on
 * business issue 13's list. The words are `HOW_YOU_PAY` in `words.ts`, NEW for the owner to read
 * but where they say otherwise.
 *
 * - "BACK TO ACCOUNT", then "Account" over "How you pay", whose heading takes the focus on arrival
 *   as every place's does (`components/RouteFocus.tsx`).
 * - THE SAVED METHOD, IN PAYMENTS' OWN CARD, with its Stop and the Stop sheet, one Stop state with
 *   Payments' (`components/SavedMethod.tsx`): saved, with Stop; her Stop sent, "Sent to the office.";
 *   stopped, once ops says so, with how to start again; and nothing saved. Ops decides (CLAUDE.md
 *   binding rule 1): nothing reads stopped, saved or changed before ops has said it.
 * - NOTHING SAVED: "You pay each invoice yourself.", and while an invoice says Stripe is on at ops,
 *   how automatic payments start: her tick on an invoice's bank screen or, since ops #70 (P57), on
 *   its card screen, both named, the only ways home has to start them, since ops has no type that
 *   starts them with nothing owed. Then the newest owed invoice whose bank screen offers the tick
 *   (`pay.offer_to_save`, which draws the card's tick too), in one line, with "Pay it from
 *   your bank account", which opens that invoice on its bank screen, handed in the history as the
 *   way back from Stripe's page hands it, never in the address (`lib/paying.ts`, `OpenOn`), its
 *   heading taking the focus at the top of the screen, as on every page opened on a way (P54). The
 *   tick there is unticked, as it always is when the screen is drawn: home never ticks it for her.
 *   One Payments holds under On its way is not offered: one somebody on the household said she sent
 *   by Zelle (P53), or she has just paid on Stripe's page, in any tab. With nothing owed at all,
 *   that she can do it when her next invoice comes; with no invoice saying Stripe is on, the first
 *   line alone.
 *   None of it while ops says a saved method is charging an owed invoice now, which a Stop does not
 *   cancel and that invoice's card on Payments says (`paysYourself`), so the two pages never say
 *   two things of one answer.
 * - HOW IT IS CHARGED, while a method stands, in the saved method's own terms: where ops holds the
 *   words she agreed under (12.1, `terms_words`, carried on this read since round six's slice S4),
 *   "In the words we hold for your automatic payment:" and those words, quoted as ops holds them,
 *   every character, the office's number in them a link held whole on its line (`HeldWords`), so
 *   the page never says less than she agreed to; where ops holds none (`chargedHow`), the bank
 *   tick's terms for a bank account, which she agreed to, and for a card, Link or a type ops does
 *   not know the lines ops prints for a saved method, never the bank tick's (the check of the
 *   Settings plan, 2026-10-17). Then how to pay one invoice by Zelle instead, with the way to
 *   Payments, and how to change, headed by the method she has, "A different card" for a card and
 *   "A different account" for any other (`differentMethod`): Stop, then either tick next time,
 *   while no Stop of hers is on its way.
 * - WAYS TO PAY EACH INVOICE, while nothing is saved, in ops's order and its words: Zelle first, and
 *   the bank and the card while Stripe is on, that the price is the same, and that she chooses on
 *   each invoice's own page; with Stripe off, Zelle and the office's number, as an invoice's own
 *   page says it. There is no preferred way to pay: ops holds none, and the order is ops's.
 * - SAVE A WAY TO PAY WITH NOTHING OWED (ops's section 12.2, round five, slice A5), where the Worker
 *   says the offer is open (`saveOffered`: its var says ops's door for setup pages is open on its
 *   host, nothing is saved and she is a granted client): "Save a bank account" and "Save a card",
 *   each beside ops's words for it, behind a fresh code, with the panel paying has
 *   (`components/SaveMethod.tsx`), between the card that says she pays each invoice herself, whose
 *   first line then names it before the ticks, and the ways to pay each invoice. Never while a method
 *   stands or the invoices are still coming down.
 * - BACK FROM STRIPE'S SETUP PAGE: How you pay is the address the Worker gives ops for that page, and
 *   ops adds `page=<kind>` and `saved=setup`, or `not-saved` (`lib/paying.ts`, `readSetupReturn`).
 *   The page says section 12.5's lines in the return page's callout under its heading, nothing of a
 *   payment: saved, with the check, and how a bank account confirmed by small deposits is; or
 *   nothing saved, with the offer under it to try again. Saved, while ops has not yet pushed the
 *   method, nothing is drawn that would say otherwise, that she pays each invoice herself, the offer
 *   or the ways to pay each, and the page reads itself again every three seconds, six times at most,
 *   so the method ops records is drawn when it comes (`useReadAgainWhile`). The lines are ops's for
 *   the moment Stripe sends her back, never a standing claim against what ops pushes after it: the
 *   page reads the address once and puts How you pay's own in its place in her history
 *   (`useSetupBack`), so the page drawn again from her history, or loaded afresh, says none of them;
 *   and once a Stop of hers says anything of the method but that it stands with Stop, sent from the
 *   page or as the Worker carries it, the saved lines are said no more, nothing waits on a push of a
 *   method, and the offer is drawn again where it is open (`useStopSaidMore`).
 * - It is read from GET /api/payments, as Payments is. While the invoices are still coming down,
 *   nothing says whether a method is saved, so the page says the rest is on its way. A member, whom
 *   that read answers 404 since she is shown no money, is sent back to Account; a 401 is the door,
 *   and a read that does not go through says so with the phone.
 */
import { useEffect, useId, useRef, useState, type ReactNode } from 'react';
import { Link, Navigate, useLocation, useNavigate } from 'react-router-dom';
import { getPayments, type Invoice, type PaymentsPlace, type SavedMethod } from '../api.ts';
import { INLINE_LINK, NAVY_BUTTON_WRAPPING } from '../components/buttons.ts';
import { Guarded, type Signed } from '../components/Guarded.tsx';
import { Phone } from '../components/Phone.tsx';
import { Checked } from '../components/ReturnWords.tsx';
import { SavedMethodCard, useStop } from '../components/SavedMethod.tsx';
import { SaveMethod } from '../components/SaveMethod.tsx';
import { Shell, useOnItsWay } from '../components/Shell.tsx';
import { HeldWords } from '../components/Terms.tsx';
import { daysOf, onItsWayLine, payableHere } from '../lib/invoices.ts';
import { readSetupReturn, type OpenOn, type SetupBack } from '../lib/paying.ts';
import type { Standing } from '../lib/standing.ts';
import { homeNameOf } from '../lib/visits.ts';
import { ACCOUNT_PATH, ACCOUNT_PAYING_PATH, invoicePath, PAYMENTS_PATH } from '../paths.ts';
import {
  ACCOUNT,
  BANK,
  CARD,
  chargedHow,
  differentMethod,
  dollars,
  HOW_YOU_PAY,
  howCharged,
  invoiceOf,
  owedInOneLine,
  PAYMENTS,
  REST_ON_ITS_WAY,
  RETURN_PAGE_WORDS,
  SETUP_RETURN_WORDS,
  ZELLE,
} from '../words.ts';
import { AccountPage } from './Account.tsx';

/** How long How you pay waits between reads while ops has not yet pushed a method she saved. */
export const SETUP_READ_GAP_MS = 3000;

/** How many times at most How you pay is read again while it waits on that push. */
export const SETUP_READS = 6;

const SECTION_HEADING = 'font-serif text-h3 font-semibold text-text-body';
const LINE = 'text-small text-pretty text-text-body';
const QUOTED = `border-l-4 border-border-subtle pl-4 whitespace-pre-line ${LINE}`;

export function AccountPaying(signed: Signed) {
  return (
    <Guarded
      load={getPayments}
      signed={signed}
      at={null}
      heading={ACCOUNT.howYouPay}
      notFound={<Navigate to={ACCOUNT_PATH} replace />}
    >
      {(place, reload) => <HowYouPay place={place} signed={signed} reload={reload} />}
    </Guarded>
  );
}

interface DrawnProps {
  readonly place: PaymentsPlace;
  readonly signed: Signed;
  readonly reload: () => void;
}

function HowYouPay({ place, signed, reload }: DrawnProps) {
  const stop = useStop(place, signed, reload);
  const { said } = stop;
  const arriving = place.arriving.invoices;
  // Back from Stripe's setup page, as its address said when the page was drawn (ops's section 12.2,
  // slice A5), which the address then says no more.
  const back = useSetupBack();
  // The saved lines are ops's for the moment she comes back, and that moment has passed once a Stop
  // of hers says anything of the method but that it stands with Stop.
  const passed = useStopSaidMore(said);
  const savedSaid = back?.saved === true && !passed;
  // Saved, said in ops's words, while ops has not yet pushed the method: nothing that would say
  // otherwise is drawn, and the page reads itself again until the push comes.
  const awaitingMethod = savedSaid && place.autopay === null;
  useReadAgainWhile(awaitingMethod, place, reload);
  // The offer to save a way to pay with nothing owed, where the Worker says it is open.
  const offered =
    place.saveOffered === true && place.autopay === null && !arriving && !awaitingMethod;
  return (
    <Shell at={null} payments={place.household.payments} over={stop.sheet}>
      <AccountPage heading={ACCOUNT.howYouPay}>
        {arriving ? <p className="mt-3 text-body text-pretty">{REST_ON_ITS_WAY}</p> : null}
      </AccountPage>

      {back === null || (back.saved && !savedSaid) ? null : <SetupReturned back={back} />}

      {arriving ? null : stop.card !== null ? (
        <SavedMethodCard {...stop.card}>
          {said?.kind === 'stopped' ? (
            <p className={`mt-2 ${LINE}`}>{HOW_YOU_PAY.startAgain}</p>
          ) : said?.kind === 'nothing_saved' ? (
            <HowToStart place={place} offered={offered} />
          ) : null}
        </SavedMethodCard>
      ) : stop.paysYourself && !awaitingMethod ? (
        <PaysYourself place={place} offered={offered} />
      ) : null}

      {offered && !stop.methodStands ? (
        <SaveMethod household={place.household.id} signed={signed} reload={reload} />
      ) : null}

      {arriving || awaitingMethod ? null : stop.methodStands && place.autopay !== null ? (
        <>
          <HowItIsCharged method={place.autopay} />
          {said?.kind === 'offered' ? <DifferentOne method={place.autopay} /> : null}
        </>
      ) : (
        <WaysToPayEach online={stripeOn(place)} />
      )}
    </Shell>
  );
}

/**
 * What the address back from Stripe's setup page says, read once as the page is first drawn and
 * kept for as long as it is drawn, and the address put back to How you pay's own in place of it in
 * her history: the lines are ops's for the moment Stripe sends her back, so the page drawn again from
 * her history, or loaded afresh, says none of them, whatever ops has pushed since. An address the
 * page does not read is left as it is.
 */
function useSetupBack(): SetupBack | null {
  const { search } = useLocation();
  const navigate = useNavigate();
  const [back] = useState(() => readSetupReturn(search));
  useEffect(() => {
    if (readSetupReturn(search) !== null) void navigate(ACCOUNT_PAYING_PATH, { replace: true });
  }, [search, navigate]);
  return back;
}

/**
 * Whether a Stop of hers has said anything of the method but that it stands with Stop: sent from the
 * page, or as the Worker carries it, on its way, stopped, nothing saved, or refused as naming another
 * method. Once it has, it has for as long as the page is drawn, so a Stop that then does not go
 * through does not bring back the lines said as she came back.
 */
function useStopSaidMore(said: Standing | null): boolean {
  const [held, setHeld] = useState(false);
  const now = said !== null && said.kind !== 'offered';
  if (now && !held) setHeld(true);
  return held || now;
}

/**
 * Reads How you pay again every three seconds, six times at most, while `waiting`: back from Stripe's
 * setup page saying saved, in ops's words, before ops has pushed the method, which ops records from
 * Stripe's event and pushes after it (its section 12.2), so the saved method is drawn when it comes
 * without a reload. A bank account Stripe confirms by small deposits is saved days later, which the
 * page says, and the reads end.
 */
function useReadAgainWhile(waiting: boolean, place: PaymentsPlace, reload: () => void) {
  const reads = useRef(0);
  useEffect(() => {
    if (!waiting || reads.current >= SETUP_READS) return;
    const timer = setTimeout(() => {
      reads.current += 1;
      reload();
    }, SETUP_READ_GAP_MS);
    return () => clearTimeout(timer);
  }, [place, waiting, reload]);
}

/**
 * What Stripe's way back from a setup page says, in section 12.5's lines, under the page's heading
 * in the return page's callout (`PaymentReturn.tsx`): saved on the bank page or the card page, the
 * check beside it, and after a bank account how one confirmed by small deposits is saved; or nothing
 * saved, after Stripe's back arrow. "Questions?" with the office's number ends it either way. Nothing
 * of a payment, since none was made.
 */
function SetupReturned({ back }: { readonly back: SetupBack }) {
  const words = SETUP_RETURN_WORDS;
  const [first, ...rest] = back.saved
    ? [
        words.savedForLater[back.page],
        ...(back.page === 'bank' ? [words.confirmYourBankSetup] : []),
      ]
    : [words.notSaved, words.leftStripeSetup];
  return (
    <div
      data-setup-return
      className="flex gap-3 rounded-card border-2 border-border-brand bg-surface-accent-soft p-5"
    >
      {back.saved ? <Checked /> : null}
      <div className="min-w-0">
        <p className="text-body font-semibold text-pretty text-text-body">{first}</p>
        {rest.map((line) => (
          <p key={line} className="mt-1 text-small text-pretty text-text-body">
            {line}
          </p>
        ))}
        <p className="mt-1 text-small text-pretty text-text-body">
          {RETURN_PAGE_WORDS.questionsBeforePhone} <Phone after="." />
        </p>
      </div>
    </div>
  );
}

/** Whether any invoice ops pushed, owed or paid, says Stripe is on at ops (`pay.online`). */
const stripeOn = (place: PaymentsPlace): boolean =>
  [...place.toPay, ...place.paid].some((invoice) => invoice.pay.online);

/**
 * Nothing saved: that she pays each invoice herself, and how automatic payments start. Never while a
 * saved method is charging an owed invoice now (`paysYourself`).
 */
function PaysYourself({
  place,
  offered,
}: {
  readonly place: PaymentsPlace;
  readonly offered: boolean;
}) {
  return (
    <div
      data-pays-yourself
      className="rounded-card border border-border-subtle bg-surface-raised px-5 py-5"
    >
      <p className="text-body font-semibold text-pretty text-text-body">
        {ACCOUNT.youPayEachInvoice}
      </p>
      <HowToStart place={place} offered={offered} />
    </div>
  );
}

/**
 * How automatic payments start, while an invoice says Stripe is on: her tick on an invoice's bank
 * screen or its card screen; then the newest owed invoice that offers it, with the way to its bank
 * screen, or, with nothing owed at all, that she can do it when her next invoice comes. Nothing
 * while no invoice says Stripe is on, since then there is no bank or card screen to start them on.
 * While the offer to save a way to pay with nothing owed is drawn below (`offered`, slice A5), the
 * first line names it before the ticks, and nothing says to wait for the next invoice; it is drawn
 * then whatever the invoices say, since ops opens that door only where Stripe is on.
 */
function HowToStart({
  place,
  offered,
}: {
  readonly place: PaymentsPlace;
  readonly offered: boolean;
}) {
  const held = useOnItsWay();
  const lineId = useId();
  if (!stripeOn(place) && !offered) return null;
  // The Worker gives what is owed newest first, so the first that offers the tick is the newest.
  const offering = place.toPay.find((invoice) => offersTheTick(invoice, held));
  const amount = offering === undefined ? null : payableHere(offering);
  return (
    <>
      <p className={`mt-2 ${LINE}`}>
        {offered ? HOW_YOU_PAY.howToStartOrSave : HOW_YOU_PAY.howToStart}
      </p>
      {offering !== undefined && amount !== null ? (
        <>
          <p id={lineId} className="mt-3 text-small text-pretty text-text-muted">
            {owedInOneLine(dayOf(offering), homeNameOf(offering, place.homes), dollars(amount))}
          </p>
          <Link
            to={invoicePath(offering.id)}
            state={{ way: 'bank' } satisfies OpenOn}
            aria-describedby={lineId}
            className={`mt-3 ${NAVY_BUTTON_WRAPPING}`}
          >
            {HOW_YOU_PAY.payFromBank}
          </Link>
        </>
      ) : place.toPay.length === 0 && !offered ? (
        <p className={`mt-2 ${LINE}`}>{HOW_YOU_PAY.nextInvoice}</p>
      ) : null}
    </>
  );
}

/**
 * Whether the invoice's bank screen draws her tick now: ops says the bank and card screens exist,
 * there is something to pay on it from here (`payableHere`: payable, no bank payment clearing, no
 * saved method paying it, a balance), ops offers to save the account she pays it from, and nothing
 * says it is on its way (`onItsWayLine`, as Payments holds it back): nobody on the household has
 * said she sent it by Zelle while the Worker says the word holds (P53), and she has not just paid
 * it on Stripe's page in any tab of her browser.
 */
const offersTheTick = (invoice: Invoice, held: (invoiceId: string) => boolean): boolean =>
  invoice.pay.online &&
  invoice.pay.offerToSave &&
  payableHere(invoice) !== null &&
  onItsWayLine(invoice, held(invoice.id)) === null;

/** The day an invoice is known by, as Payments names it: its clean's day, else its issue day's. */
function dayOf(invoice: Invoice): string | null {
  const days = daysOf(invoice);
  if (days === null) return null;
  return 'visit' in days ? days.visit : invoiceOf(days.issued);
}

/** A section of the page under its heading, read by it. */
function Section({
  heading,
  children,
}: {
  readonly heading: string;
  readonly children: ReactNode;
}) {
  const headingId = useId();
  return (
    <section aria-labelledby={headingId} className="flex flex-col gap-2.5">
      <h2 id={headingId} className={SECTION_HEADING}>
        {heading}
      </h2>
      {children}
    </section>
  );
}

/**
 * How the saved method is charged, in its own terms: the words ops holds for it, where it holds
 * them, as it holds them; else `chargedHow`'s lines. Then how to pay one invoice by Zelle instead.
 */
function HowItIsCharged({ method }: { readonly method: SavedMethod }) {
  const held = method.words !== undefined && method.words.trim() !== '' ? method.words : null;
  return (
    <Section heading={howCharged(method.methodType)}>
      {held === null ? (
        chargedHow(method.label, method.methodType).map((line) => (
          <p key={line} className={LINE}>
            {line}
          </p>
        ))
      ) : (
        <>
          <p className={LINE}>{HOW_YOU_PAY.inTheWordsWeHold}</p>
          <blockquote className={QUOTED}>
            <HeldWords words={held} />
          </blockquote>
        </>
      )}
      <p className={LINE}>
        {HOW_YOU_PAY.zelleInsteadBefore}{' '}
        <Link to={PAYMENTS_PATH} className={INLINE_LINK}>
          {PAYMENTS.eyebrow}
        </Link>{' '}
        {HOW_YOU_PAY.zelleInsteadAfter}
      </p>
    </Section>
  );
}

/**
 * How to change to a different one, headed by the method she has, a different card or a different
 * account: Stop this one, then either tick the next time. Not while her Stop is on its way, which
 * is that first step already.
 */
function DifferentOne({ method }: { readonly method: SavedMethod }) {
  return (
    <Section heading={differentMethod(method.methodType)}>
      <p className={LINE}>{HOW_YOU_PAY.stopThisFirst}</p>
    </Section>
  );
}

/**
 * The ways to pay each invoice, in ops's order and words: Zelle, then the bank and the card while
 * Stripe is on, the price the same however she pays, and that she chooses on each invoice's own
 * page; with Stripe off, Zelle, and the office's number for any other way.
 */
function WaysToPayEach({ online }: { readonly online: boolean }) {
  const ways = online ? [ZELLE.heading, BANK.heading, CARD.heading] : [ZELLE.heading];
  return (
    <Section heading={HOW_YOU_PAY.waysToPay}>
      <ul className="flex list-disc flex-col gap-1 pl-6">
        {ways.map((way) => (
          <li key={way} className={LINE}>
            {way}
          </li>
        ))}
      </ul>
      {online ? null : (
        <p className={LINE}>
          {ZELLE.cantUseBeforePhone} <Phone /> {ZELLE.cantUseAfterPhone}
        </p>
      )}
      <p className={LINE}>{ZELLE.samePrice}</p>
      {online ? <p className={LINE}>{HOW_YOU_PAY.chooseEachTime}</p> : null}
    </Section>
  );
}
